'use client';

import Link from 'next/link';
import { useState } from 'react';
import { PATH } from '@/constants';
import type { CaseStudy } from '../data';
import { ALL_STUDIES } from '../data';
import { CustomerMark, LinePill } from './Atoms';

const FILTERS = ['All', 'Sales', 'Marketing'] as const;
type Filter = (typeof FILTERS)[number];

export function StoryCard({ s }: { s: CaseStudy }) {
  return (
    <Link
      href={`${PATH.CASE_STUDIES}/${s.slug}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: '1px solid var(--mb-border)',
        borderRadius: 18,
        padding: 28,
        textDecoration: 'none',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(-4px)';
        el.style.boxShadow = '0 16px 32px -12px rgba(2,16,55,.14)';
        el.style.borderColor = 'var(--mb-border-strong)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = '';
        el.style.boxShadow = '';
        el.style.borderColor = 'var(--mb-border)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <CustomerMark name={s.customer} size={16} />
        <LinePill line={s.line} />
      </div>
      <h3
        style={{
          fontFamily: 'var(--mb-font-display)',
          fontWeight: 600,
          fontSize: '1.2rem',
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
          color: 'var(--mb-slate-900)',
          margin: '0 0 20px',
        }}
      >
        {s.cardTitle}
      </h3>
      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          paddingTop: 18,
          borderTop: '1px solid var(--mb-border)',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--mb-font-display)',
              fontWeight: 700,
              fontSize: '1.55rem',
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: 'var(--mb-blue-600)',
            }}
          >
            {s.stat}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--mb-slate-500)', marginTop: 5 }}>{s.statLabel}</div>
        </div>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--mb-blue-600)' }}>{s.readTime} read →</span>
      </div>
    </Link>
  );
}

export default function StoryGrid() {
  const [filter, setFilter] = useState<Filter>('All');
  const visible =
    filter === 'All'
      ? ALL_STUDIES
      : ALL_STUDIES.filter((s) => (filter === 'Sales' ? s.line === 'sales' : s.line === 'marketing'));

  return (
    <section style={{ padding: 'clamp(40px, 5vw, 64px) clamp(20px, 5vw, 80px) 0' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32, justifyContent: 'center', flexWrap: 'wrap' }}>
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'var(--mb-font-body)',
                  border: `1px solid ${active ? 'var(--mb-blue-600)' : 'var(--mb-border)'}`,
                  background: active ? 'var(--mb-blue-600)' : '#fff',
                  color: active ? '#fff' : 'var(--mb-slate-700)',
                  transition: 'all 150ms ease',
                }}
              >
                {f}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div
          className="cs-story-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}
        >
          {visible.map((s) => (
            <StoryCard key={s.slug} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
